import { createTheme } from '@mui/material/styles';

/**
 * Design tokens for the public website (not the CMS / dashboards).
 * Mirrored as CSS variables in core/styles/public.css.
 */
export const brand = {
    primary: '#4a5bb0',
    primaryDark: '#3d4c96',
    primaryLight: '#eef1f8',
    ink: '#0f172a',
    slate: '#475569',
    muted: '#64748b',
    line: '#e2e8f0',
    surface: '#f8fafc'
};

const fontStack = "'Inter', 'kurdireg', system-ui, -apple-system, 'Segoe UI', Roboto, sans-serif";

const publicTheme = createTheme({
    palette: {
        primary: { main: brand.primary, dark: brand.primaryDark, light: '#8f9dd0', contrastText: '#fff' },
        secondary: { main: '#5b8db8' },
        success: { main: '#059669', dark: '#047857' },
        text: { primary: brand.ink, secondary: brand.slate, disabled: '#94a3b8' },
        divider: brand.line,
        background: { default: brand.surface, paper: '#ffffff' },
        grey: { 50: brand.surface, 100: '#f1f5f9', 200: brand.line }
    },
    shape: { borderRadius: 12 },
    typography: {
        fontFamily: fontStack,
        h1: { fontWeight: 800, letterSpacing: '-0.03em' },
        h2: { fontWeight: 800, letterSpacing: '-0.025em' },
        h3: { fontWeight: 700, letterSpacing: '-0.02em' },
        h4: { fontWeight: 700, letterSpacing: '-0.02em' },
        h5: { fontWeight: 700, letterSpacing: '-0.015em' },
        h6: { fontWeight: 650, letterSpacing: '-0.01em' },
        subtitle1: { fontWeight: 600 },
        button: { textTransform: 'none', fontWeight: 600, letterSpacing: 0 }
    },
    components: {
        MuiButton: {
            defaultProps: { disableElevation: true },
            styleOverrides: {
                root: { borderRadius: 10, paddingInline: 18, transition: 'all .15s ease' },
                containedPrimary: {
                    boxShadow: '0 1px 2px rgba(74,91,176,.25)',
                    '&:hover': { boxShadow: '0 6px 16px rgba(74,91,176,.28)', transform: 'translateY(-1px)' }
                },
                outlined: { borderColor: brand.line, color: brand.ink, '&:hover': { borderColor: brand.primary, background: brand.primaryLight } }
            }
        },
        MuiCard: {
            defaultProps: { elevation: 0 },
            styleOverrides: {
                root: {
                    borderRadius: 16,
                    border: `1px solid ${brand.line}`,
                    boxShadow: '0 1px 2px rgba(15,23,42,.04)',
                    transition: 'box-shadow .2s ease, border-color .2s ease, transform .2s ease'
                }
            }
        },
        MuiPaper: {
            styleOverrides: {
                rounded: { borderRadius: 16 },
                outlined: { borderColor: brand.line }
            }
        },
        MuiChip: {
            styleOverrides: {
                root: { borderRadius: 8, fontWeight: 600 },
                outlined: { borderColor: brand.line, background: '#fff' },
                colorPrimary: { '&.MuiChip-outlined': { background: brand.primaryLight, borderColor: 'transparent', color: brand.primaryDark } }
            }
        },
        MuiOutlinedInput: {
            styleOverrides: {
                root: {
                    borderRadius: 10,
                    background: '#fff',
                    '&:hover .MuiOutlinedInput-notchedOutline': { borderColor: '#cbd5e1' },
                    '&.Mui-focused .MuiOutlinedInput-notchedOutline': { borderColor: brand.primary, borderWidth: 1.5 }
                },
                notchedOutline: { borderColor: brand.line }
            }
        },
        MuiTooltip: { styleOverrides: { tooltip: { borderRadius: 8, background: brand.ink, fontSize: 12 } } },
        MuiLink: { styleOverrides: { root: { textUnderlineOffset: 3 } } }
    }
});

export default publicTheme;
